import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Radio from 'src/components/form-components/Radio/Radio';
import Placeholder from 'src/components/Placeholder/Placeholder';
import { PaymentType } from 'src/entities/company';
import { IPaymentSelectOption, IPocket, IProviderMessage, PaymentProvider, PocketType } from 'src/entities/pocket';
import { getSimplePayPaymentInformationUrl } from 'src/services/api/paymentReservation';

const messages = defineMessages({
    onSitePayment: {
        id: 'onSitePayment',
        defaultMessage: 'Helyszíni fizetés'
    },
    onBarionPayment: {
        id:'onBarionPayment',
        defaultMessage: 'Bankkártyás fizetés Barionnal'
    },
    onPayPalPayment: {
        id:'onPayPalPayment',
        defaultMessage: 'PayPal'
    },
    onSimplePayPayment: {
        id:'onSimplePayPayment',
        defaultMessage: 'Bankkártyás fizetés Simplepay-el'  // TODO: Fordítás a nyelvi json file-okban.
    }
})

const PayPalImg = <img src={'https://www.paypalobjects.com/webstatic/en_US/i/buttons/cc-badges-ppmcvdam.png'} />;
const BarionImg = <img src={'https://d1ursyhqs5x9h1.cloudfront.net/sw/images/Barion-smart-payment-horizontal-whitebg.png'} />;

export interface IPaymentSelectProps {
    pockets: IPocket[];
    paymentType: PaymentType;
    onChange: (value: IPaymentSelectOption) => void;
    selected: IPaymentSelectOption | null;
}

const PaymentSelect: React.FunctionComponent<IPaymentSelectProps> = ({ pockets, paymentType, onChange, selected }) => {

    const [hasInit, setHasInit] = useState(false);
    const selectOptions: IPaymentSelectOption[] = createOptions();
    const intl = useIntl();

    useEffect(() => {

        if (hasInit || !pockets.length) {
            return;
        }

        const selectedPocket = selected ? (selectOptions.find(opt => opt.Id === selected.Id) ?? selectOptions[0]) : selectOptions[0];
        onChange(selectedPocket);
        setHasInit(true);

    }, [pockets, hasInit]);

    function createOptions() {
        /* eslint-disable-next-line no-bitwise */
        const result: IPaymentSelectOption[] = pockets.filter(pocket => (PocketType.Online === (pocket.Type & PocketType.Online)) && pocket.IsActive);


        if (paymentType === PaymentType.NeedAccept || paymentType === PaymentType.NoNeedToPay) {
            result.push({
                Id: -1,
                Name: messages.onSitePayment.defaultMessage,
                PaymentProvider: PaymentProvider.NoProvider,
                NeedsInvoice: false,
                DisplayName: messages.onSitePayment
            });
        }
 
        return addLocalizedDisplayNameToOptions(result);
    }

    function addLocalizedDisplayNameToOptions(result: IPaymentSelectOption[]): IPaymentSelectOption[] {
        const providerMessages: Partial<Record<PaymentProvider, IProviderMessage>> = {
          [PaymentProvider.Barion]: {
            displayName: messages.onBarionPayment,
            name: messages.onBarionPayment.defaultMessage
          },
          [PaymentProvider.Paypal]: {
            displayName: messages.onPayPalPayment,
            name: messages.onPayPalPayment.defaultMessage
          },
          [PaymentProvider.SimplePay]: {
            displayName: messages.onSimplePayPayment,
            name: messages.onSimplePayPayment.defaultMessage
          }
          // Add more payment providers here if needed
        };
      
        return result.map(o => {
          const providerMessage = o.PaymentProvider ? providerMessages[o.PaymentProvider] : undefined;
          if (providerMessage) {
            return {
              ...o,
              DisplayName: providerMessage.displayName,
              Name: providerMessage.name
            };
          }
      
          return o;
        });
    }

    const SimplePayImg = (
        <a href={getSimplePayPaymentInformationUrl(intl.locale)} target="_blank" rel="noreferrer" >
            <img alt="SimplePay Bankcard Logos"
                src='https://s3.eu-central-1.amazonaws.com/booked4.us/Common/img/simplepay/simplepay_bankcard_logos.png' />
        </a>
    );
      
    return (
        (selectOptions && selected) ?
            <Radio<IPaymentSelectOption>
                options={selectOptions}
                selected={(payment) => payment.Id === selected.Id}
                onChange={onChange}
                rootClassName="payment"
                buttonClassName="payment-method"
                labelClassName="payment-method__label"
                inputClassName="payment-method__input"
                getLabel={(payment, checked) =>
                    <div className={classNames(
                        'pocket',
                        { 'pocket--checked': checked })} >
                        <span className={'pocket__name'}>
                            {payment.DisplayName
                                ? <FormattedMessage {...payment.DisplayName} />
                                : payment.Name
                            }
                        </span>
                        {payment.PaymentProvider === PaymentProvider.SimplePay && SimplePayImg}
                        {payment.PaymentProvider === PaymentProvider.Paypal && PayPalImg}
                        {payment.PaymentProvider === PaymentProvider.Barion && BarionImg}
                    </div>}
                name={'payment'}
            />
            :
            <Placeholder count={2} />
    )
}

export default PaymentSelect;